/**
 * Service registry for managing RPC services
 */

import {
  ServiceDescriptor,
  MethodDescriptor,
  ServiceContext,
} from "../types/protocol";
import { ErrorCode, RpcCallError } from "../types/errors";
import { ServiceValidator } from "./ServiceValidator";

export class ServiceRegistry {
  private services = new Map<string, any>();

  /**
   * Register a service implementation
   */
  register(name: string, implementation: any): void {
    if (this.services.has(name)) {
      throw new Error(`Service already registered: ${name}`);
    }

    // Use ServiceValidator for comprehensive validation
    ServiceValidator.validateService(name, implementation);

    this.services.set(name, implementation);
  }

  /**
   * Unregister a service
   */
  unregister(name: string): void {
    this.services.delete(name);
  }

  /**
   * Invoke a service method
   */
  async invoke(service: string, method: string, payload: any): Promise<any> {
    const impl = this.services.get(service);

    if (!impl) {
      throw new RpcCallError({
        code: ErrorCode.SERVICE_NOT_FOUND,
        message: `Service not found: ${service}`,
      });
    }

    const fn = impl[method];

    if (typeof fn !== "function") {
      throw new RpcCallError({
        code: ErrorCode.METHOD_NOT_FOUND,
        message: `Method not found: ${service}.${method}`,
      });
    }

    try {
      // Create context for service call
      const context: ServiceContext = {};

      // Call method with payload and context
      const result = await fn.call(impl, payload, context);
      return result;
    } catch (error) {
      // Re-throw RpcCallError as-is
      if (error instanceof RpcCallError) {
        throw error;
      }

      // Wrap other errors in RpcCallError
      throw new RpcCallError({
        code: ErrorCode.SERVICE_ERROR,
        message:
          error instanceof Error ? error.message : "Unknown service error",
        data: error,
      });
    }
  }

  /**
   * List all registered service names
   */
  listServices(): string[] {
    return Array.from(this.services.keys());
  }

  /**
   * Get service descriptor with method information
   */
  getServiceDescriptor(serviceName: string): ServiceDescriptor {
    const impl = this.services.get(serviceName);

    if (!impl) {
      throw new Error(`Service not found: ${serviceName}`);
    }

    const methodNames = ServiceValidator.getServiceMethods(impl);
    const methods: MethodDescriptor[] = methodNames.map((name) => ({
      name,
      // TODO: Add parameter introspection in future version
      params: [],
      returns: "any",
    }));

    return {
      name: serviceName,
      methods,
    };
  }

  /**
   * Check if service has a specific method
   */
  hasMethod(serviceName: string, methodName: string): boolean {
    const impl = this.services.get(serviceName);
    return impl && typeof impl[methodName] === "function";
  }

  /**
   * Clear all registered services (useful for testing)
   */
  clear(): void {
    this.services.clear();
  }

  /**
   * Get the number of registered services
   */
  size(): number {
    return this.services.size;
  }
}
